import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { ExternalLink, Link2, Search } from 'lucide-react';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '../../../../../components/ui/popover';
import { CellFkRowPicker } from './CellFkRowPicker';
import { useAnalytics } from '../../../../../core/analytics';
import type { LooseRelationship } from '../../../../../types/electron';
import type { TableExplorerRelationshipPort } from '../../../table-explorer/components/tableExplorerHostPorts';
import { cn } from '../../../../../lib/utils';

/** One row in the popover. For real FK there's one; for loose FK polymorphic dispatch, N. */
export type FkPickerTarget = {
  schema: string;
  table: string;
  column: string;
  isLoose: boolean;
  /** Optional loose-rel record — drives targetFilter / sourcePath when opening the picker dialog. */
  looseRel?: LooseRelationship;
};

export type FkCellEditorProps = {
  initialValue: unknown;
  connectionId: string;
  database: string;
  engine: string;
  /** Source column the cell belongs to — threaded into the picker dialog. */
  sourceSchema: string;
  sourceTable: string;
  sourceColumn: string;
  sourceDataType?: string;
  /** Set of FK targets the user can search. ≥1 entry. */
  targets: readonly FkPickerTarget[];
  /** Host port (Azure Table): the row picker reads through it, never SQL (M7). */
  relationshipPort?: TableExplorerRelationshipPort;
  /** The host refused the last commit (S4 review G, G5). SQL never sets it: its input is unchanged. */
  invalid?: boolean;
  onCommit: (value: unknown) => void;
  onCancel: () => void;
  inputClassName?: string;
  containerClassName?: string;
};

function formatForEdit(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseEditedValue(text: string, originalValue: unknown): unknown {
  if (text === '') return null;
  if (typeof originalValue === 'number') {
    const n = Number(text);
    if (!Number.isNaN(n)) return n;
  }
  if (typeof originalValue === 'boolean') {
    const lower = text.toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }
  return text;
}

export function FkCellEditor({
  initialValue,
  connectionId,
  database,
  engine,
  sourceSchema,
  sourceTable,
  sourceColumn,
  sourceDataType,
  targets,
  relationshipPort,
  invalid,
  onCommit,
  onCancel,
  inputClassName = 'block w-full h-6 pl-1 pr-6 text-xs bg-background border border-primary rounded-sm outline-none',
  containerClassName = 'relative w-full',
}: FkCellEditorProps) {
  const [text, setText] = useState(() => formatForEdit(initialValue));
  // Defer initial open by one tick — see EnumCellEditor for the rationale.
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState<FkPickerTarget | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { track } = useAnalytics();

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
      setPopoverOpen(true);
    });
  }, []);

  const commitText = useCallback(() => {
    const parsed = parseEditedValue(text, initialValue);
    const original = initialValue === undefined ? null : initialValue;
    if (parsed === original || String(parsed) === String(original)) {
      onCancel();
      return;
    }
    onCommit(parsed);
  }, [text, initialValue, onCommit, onCancel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitText();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (pickerTarget) {
          setPickerTarget(null);
          return;
        }
        if (popoverOpen) {
          setPopoverOpen(false);
          return;
        }
        onCancel();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setPopoverOpen(true);
      }
    },
    [commitText, popoverOpen, pickerTarget, onCancel],
  );

  const openPicker = useCallback(
    (target: FkPickerTarget) => {
      track('feature:cell_edit_fk_picker_opened', { engine, isLoose: target.isLoose });
      setPopoverOpen(false);
      setPickerTarget(target);
    },
    [engine, track],
  );

  const handlePicked = useCallback(
    (value: string | number | boolean | Date) => {
      track('feature:cell_edit_fk_picked', { engine, isLoose: pickerTarget?.isLoose ?? false });
      setPickerTarget(null);
      onCommit(value);
    },
    [engine, onCommit, track, pickerTarget],
  );

  // Suppress blur-commit while popover / picker is open so the cell doesn't
  // auto-commit empty text when the user clicks an item.
  const suppressBlur = popoverOpen || pickerTarget !== null;

  return (
    <div className={containerClassName} data-testid="fk-cell-editor">
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverAnchor asChild>
          <input
            ref={inputRef}
            type="text"
            value={text}
            translate="no"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setPopoverOpen(true)}
            onBlur={() => {
              if (suppressBlur) return;
              commitText();
            }}
            className={invalid ? cn(inputClassName, 'border-destructive') : inputClassName}
            {...(invalid ? { 'aria-invalid': true } : {})}
            data-testid="fk-cell-editor-input"
          />
        </PopoverAnchor>
        <PopoverContent
          className="w-72 p-1"
          align="start"
          sideOffset={2}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
          data-testid="fk-cell-editor-popover"
        >
          {targets.map((target, i) => (
            <button
              key={`${target.schema}.${target.table}.${target.column}-${i}`}
              type="button"
              className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-left rounded-sm hover:bg-accent"
              onPointerDown={(e) => {
                e.preventDefault();
                openPicker(target);
              }}
              data-testid="fk-cell-editor-search-row"
              data-loose={target.isLoose ? 'true' : 'false'}
            >
              <Search className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate">
                Search row in{' '}
                <span translate="no" className="font-medium">
                  {target.schema ? `${target.schema}.${target.table}` : target.table}
                </span>
                …
              </span>
            </button>
          ))}
        </PopoverContent>
      </Popover>
      {(() => {
        // Match the FK icon convention used everywhere else: Link2 for loose,
        // ExternalLink for real (see LazyForeignKeyCell.tsx and the loose-FK
        // doc table). Pick the icon from the first target — when targets mix
        // (rare; theoretically a column could have both a real FK and loose
        // declarations), the loose icon wins because the popover lists the
        // loose options first.
        const anyLoose = targets.some((t) => t.isLoose);
        const Icon = anyLoose ? Link2 : ExternalLink;
        const triggerClass = anyLoose
          ? 'absolute inset-y-0 right-0 inline-flex w-5 items-center justify-center text-teal-600 dark:text-teal-400 hover:text-teal-500'
          : 'absolute inset-y-0 right-0 inline-flex w-5 items-center justify-center text-blue-600 dark:text-blue-400 hover:text-blue-500';
        return (
          <button
            type="button"
            title="Search related row"
            aria-label="Search related row"
            onPointerDown={(e) => {
              e.preventDefault();
              setPopoverOpen((v) => !v);
              inputRef.current?.focus();
            }}
            className={triggerClass}
            data-testid="fk-cell-editor-chevron"
            data-loose={anyLoose ? 'true' : 'false'}
          >
            <Icon className="h-3 w-3" aria-hidden="true" />
          </button>
        );
      })()}
      {pickerTarget && (
        <CellFkRowPicker
          target={pickerTarget}
          connectionId={connectionId}
          database={database}
          engine={engine}
          sourceSchema={sourceSchema}
          sourceTable={sourceTable}
          sourceColumn={sourceColumn}
          sourceDataType={sourceDataType}
          initialValue={initialValue}
          relationshipPort={relationshipPort}
          onClose={() => setPickerTarget(null)}
          onPicked={handlePicked}
          returnFocusRef={inputRef}
          onClearToNull={() => {
            setPickerTarget(null);
            onCommit(null);
          }}
        />
      )}
    </div>
  );
}
